/**
 * PaymentRecordsPage — Issue #134
 *
 * 收款记录管理页面：
 *   - 列表展示（按 orderProjectId 过滤）
 *   - 创建收款记录
 *   - 财务确认到账（confirm）
 *   - 作废（void）
 *   - 冲正（reverse）
 *
 * 所有 Dialog 涉及编辑数据时设置 key 强制重挂载。
 * 不得手写 fetch/axios，使用 useFinance hooks。
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Decimal from 'decimal.js';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  usePaymentRecords,
  usePaymentRecordActions,
  type PaymentRecordResponseDto,
  type CreatePaymentRecordDto,
} from '@/hooks/useFinance';

/** Format a Decimal/string amount as CNY with 2 decimal places. */
function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Status label helpers
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  pending_confirm: '待确认',
  confirmed: '已确认',
  voided: '已作废',
  reversed: '已冲正',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: '现金',
  card: '刷卡',
  transfer: '银行转账',
  wechat: '微信',
  alipay: '支付宝',
  corporate: '对公',
  installment: '分期',
};

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    pending_confirm: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    voided: 'bg-gray-100 text-gray-600',
    reversed: 'bg-red-100 text-red-700',
  };
  const cls = colorMap[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface CreateDialogProps {
  key: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreatePaymentDialog({ open, onClose, onCreated }: Omit<CreateDialogProps, 'key'>) {
  const { createRecord, loading, error } = usePaymentRecordActions();
  const [form, setForm] = useState<{
    orderId?: string;
    orderProjectId?: string;
    amount: string;
    paymentMethod: string;
    paidAt?: string;
    note?: string;
  }>({
    orderProjectId: '',
    amount: '',
    paymentMethod: 'cash',
    note: '',
  });

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = await createRecord(form as CreatePaymentRecordDto);
    if (result) {
      onCreated();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">新建收款记录</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error.message}</div>
        )}
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">订单项目 ID</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.orderProjectId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, orderProjectId: e.target.value }))}
              placeholder="order_project_id"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">收款金额</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="例：1000.00"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">收款方式</label>
            <select
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.paymentMethod}
              onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
            >
              {Object.entries(PAYMENT_METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">备注</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={form.note ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? '提交中…' : '确认创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Void Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface VoidDialogProps {
  key: string;
  record: PaymentRecordResponseDto | null;
  onClose: () => void;
  onVoided: () => void;
}

function VoidDialog({ record, onClose, onVoided }: Omit<VoidDialogProps, 'key'>) {
  const { voidRecord, loading, error } = usePaymentRecordActions();
  const [reason, setReason] = useState('');

  if (!record) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!record) return;
    const result = await voidRecord(record.id, {
      expectedVersion: record.version,
      voidReason: reason,
    });
    if (result) {
      onVoided();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold">作废收款记录</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error.message}</div>
        )}
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">作废原因</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={1}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100">取消</button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? '处理中…' : '确认作废'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function PaymentRecordsPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.PAYMENT_RECORD_READ);
  const canCreate = hasPermission(permissions, FINANCE_PERMISSIONS.PAYMENT_RECORD_CREATE);
  const canConfirm = hasPermission(permissions, FINANCE_PERMISSIONS.PAYMENT_RECORD_CONFIRM);
  const canVoid = hasPermission(permissions, FINANCE_PERMISSIONS.PAYMENT_RECORD_VOID);
  const canReverse = hasPermission(permissions, FINANCE_PERMISSIONS.PAYMENT_RECORD_REVERSE);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看收款记录的权限。
      </div>
    );
  }

  const { records, loading, error, fetchRecords } = usePaymentRecords();
  const { confirmRecord, reverseRecord, loading: actionLoading } = usePaymentRecordActions();

  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Dialog state — key forces remount on each open
  const [createDialogKey, setCreateDialogKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [voidTarget, setVoidTarget] = useState<PaymentRecordResponseDto | null>(null);
  const [voidDialogKey, setVoidDialogKey] = useState(0);

  useEffect(() => {
    void fetchRecords({
      orderProjectId: filterProjectId || undefined,
      status: filterStatus || undefined,
    });
  }, [fetchRecords, filterProjectId, filterStatus]);

  async function handleConfirm(record: PaymentRecordResponseDto) {
    if (!confirm(`确认到账：¥${record.amount}？`)) return;
    const result = await confirmRecord(record.id, { expectedVersion: record.version });
    if (result) {
      void fetchRecords({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
    }
  }

  async function handleReverse(record: PaymentRecordResponseDto) {
    if (!confirm(`冲正此笔收款：¥${record.amount}？将创建一条负数冲正记录。`)) return;
    const result = await reverseRecord(record.id, { expectedVersion: record.version });
    if (result) {
      void fetchRecords({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
    }
  }

  function openVoidDialog(record: PaymentRecordResponseDto) {
    setVoidTarget(record);
    setVoidDialogKey((k) => k + 1);
  }

  function openCreateDialog() {
    setCreateDialogKey((k) => k + 1);
    setShowCreate(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">收款记录</h1>
        {canCreate && (
          <button
            onClick={openCreateDialog}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            新建收款
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="订单项目 ID 过滤"
          value={filterProjectId}
          onChange={(e) => setFilterProjectId(e.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">加载中…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">项目 ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">金额</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">方式</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">创建时间</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">暂无记录</td>
                </tr>
              ) : (
                records.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.id.slice(0, 8)}…</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.orderProjectId?.slice(0, 8) ?? '—'}…</td>
                    <td className="px-4 py-3 font-semibold">{fmtCny(r.amount)}</td>
                    <td className="px-4 py-3">{PAYMENT_METHOD_LABELS[r.paymentMethod] ?? r.paymentMethod}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-gray-500">{new Date(r.createdAt).toLocaleDateString('zh-CN')}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {r.status === 'pending_confirm' && (
                          <>
                            {canConfirm && (
                              <button
                                onClick={() => { void handleConfirm(r); }}
                                disabled={actionLoading}
                                className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                确认到账
                              </button>
                            )}
                            {canVoid && (
                              <button
                                onClick={() => openVoidDialog(r)}
                                disabled={actionLoading}
                                className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                              >
                                作废
                              </button>
                            )}
                          </>
                        )}
                        {r.status === 'confirmed' && canReverse && (
                          <button
                            onClick={() => { void handleReverse(r); }}
                            disabled={actionLoading}
                            className="rounded bg-red-100 px-2 py-1 text-xs text-red-700 hover:bg-red-200 disabled:opacity-50"
                          >
                            冲正
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialogs — key forces remount */}
      <CreatePaymentDialog
        key={createDialogKey}
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          void fetchRecords({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
        }}
      />
      <VoidDialog
        key={voidDialogKey}
        record={voidTarget}
        onClose={() => setVoidTarget(null)}
        onVoided={() => {
          void fetchRecords({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
        }}
      />
    </div>
  );
}
